import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PasswordInput from './PasswordInput';
import { showConfirm } from './ConfirmDialog';
import {
  apiAiCredits,
  apiGetImportStatus,
  apiSaveTmKey,
  apiDeleteTmKey,
  apiRunTmImport,
  apiImportMtCsv,
  type AiCreditsInfo,
  type TmStatusResult,
} from '../api';
import { useWriteGate } from '../lib/writeGate';

/**
 * BandImportSection — TourManager (TM) one-click import + MasterTour (MT) CSV
 * import, rendered as a band-scoped section inside Band Settings (above the
 * Backup & restore section). Reserved for the Unlimited plan (server enforces
 * via /api/import → 403). (2026-08-26)
 *
 * The user's TourManager API key is a SECRET: it is never sent back by the
 * backend (GET /api/import/tm-status only returns { hasKey }). The input is a
 * password field and we never pre-fill it with the real key — only a masked
 * placeholder. If the field is left empty / still masked on save, nothing is
 * sent (the backend also ignores masked values).
 *
 * LAYOUT RULE: this component renders INSIDE BandSettingsPage's single `.card`
 * (Advanced tab). Its blocks are `.form-section` sub-sections (hairline
 * separator, no border box) — NEVER `.card`, which would nest a frame in a
 * frame. Canon: "Foldable cards" / UI homogeneity rules.
 */
export default function BandImportSection({ bandId, bandName }: { bandId: string; bandName: string }) {
  const { t } = useTranslation();
  // Capability-aware write gate (lot A2). `POST /api/import/*` (+ the TM key
  // routes) are mounted AFTER the server's global read-only guard
  // (server/index.js:655) → 403 for a `users.role = 'read-only'` account.
  // Freeze the 4 write surfaces (save/delete key, TM run, MT CSV import).
  const writeGate = useWriteGate(bandId);
  const canWrite = writeGate.canWrite;

  // ---- Unlimited gating (matches the server's isUnlimited flag) ----
  const [credits, setCredits] = useState<AiCreditsInfo | null>(null);

  // ---- TM key management ----
  const [tmStatus, setTmStatus] = useState<TmStatusResult | null>(null);
  const [keyInput, setKeyInput] = useState('');
  const [keyMsg, setKeyMsg] = useState<string | null>(null);
  const [keyErr, setKeyErr] = useState<string | null>(null);
  const [keyBusy, setKeyBusy] = useState(false);

  // ---- TM one-click run ----
  const [tmRunning, setTmRunning] = useState(false);
  const [tmResult, setTmResult] = useState<string | null>(null);
  const [tmErr, setTmErr] = useState<string | null>(null);

  // ---- MT CSV import ----
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvResult, setCsvResult] = useState<string | null>(null);
  const [csvErr, setCsvErr] = useState<string | null>(null);

  const unlimited = credits?.unlimited === true;
  const label = bandName || t('importPage.currentBand');

  useEffect(() => {
    apiAiCredits().then(setCredits).catch(() => setCredits(null));
  }, []);

  // Only query the key status once we know the user is Unlimited (the route
  // 403s otherwise, which is expected).
  useEffect(() => {
    if (!unlimited) return;
    let cancelled = false;
    apiGetImportStatus()
      .then((s) => { if (!cancelled) setTmStatus(s); })
      .catch(() => { if (!cancelled) setTmStatus(null); });
    return () => { cancelled = true; };
  }, [unlimited]);

  const saveKey = async () => {
    if (!canWrite) return; // frozen for a global read-only account (server 403s)
    setKeyMsg(null); setKeyErr(null);
    const val = keyInput.trim();
    // Never send a fake value: an empty or still-masked field means "unchanged".
    if (!val || val.startsWith('***')) return;
    setKeyBusy(true);
    try {
      await apiSaveTmKey(val);
      setTmStatus({ ok: true, hasKey: true });
      setKeyInput('');
      setKeyMsg(t('importPage.keySaved'));
    } catch (err) {
      setKeyErr(err instanceof Error ? err.message : t('importPage.keySaveFailed'));
    } finally {
      setKeyBusy(false);
    }
  };

  const deleteKey = async () => {
    if (!canWrite) return; // before the confirm dialog AND the API call
    if (!(await showConfirm(t('importPage.deleteKeyConfirm')))) return;
    setKeyMsg(null); setKeyErr(null); setKeyBusy(true);
    try {
      await apiDeleteTmKey();
      setTmStatus({ ok: true, hasKey: false });
      setKeyInput('');
      setKeyMsg(t('importPage.keyDeleted'));
    } catch (err) {
      setKeyErr(err instanceof Error ? err.message : t('importPage.keyDeleteFailed'));
    } finally {
      setKeyBusy(false);
    }
  };

  const runTmImport = async () => {
    if (!canWrite) return; // frozen for a global read-only account (server 403s)
    if (!bandId) return;
    setTmResult(null); setTmErr(null); setTmRunning(true);
    try {
      const r = await apiRunTmImport(bandId);
      setTmResult(t('importPage.tmResult', {
        shows: r.shows?.created ?? 0,
        events: r.events ?? 0,
        timezones: r.timezones?.updated ?? 0,
        hotels: r.hotels?.created ?? 0,
        contacts: r.contacts?.created ?? 0,
      }));
    } catch (err) {
      setTmErr(err instanceof Error ? err.message : t('importPage.tmRunFailed'));
    } finally {
      setTmRunning(false);
    }
  };

  const importMtCsv = async () => {
    if (!canWrite) return; // frozen for a global read-only account (server 403s)
    if (!bandId || !csvFile) return;
    setCsvResult(null); setCsvErr(null); setCsvImporting(true);
    try {
      const csv = await csvFile.text();
      const r = await apiImportMtCsv(bandId, csv);
      setCsvResult(t('importPage.mtResult', {
        rows: r.rows ?? 0,
        created: r.shows?.created ?? 0,
        skipped: r.shows?.skipped ?? 0,
      }));
      setCsvFile(null);
    } catch (err) {
      setCsvErr(err instanceof Error ? err.message : t('importPage.mtImportFailed'));
    } finally {
      setCsvImporting(false);
    }
  };

  // ---- All hooks live above these early returns (React #310). ----
  if (credits === null) {
    return (
      <p style={{ color: 'var(--text-dim)' }}>{t('common.loading')}</p>
    );
  }

  if (!unlimited) {
    return (
      <div className="form-section" style={{ maxWidth: 520 }}>
        <p style={{ marginTop: 0 }}>{t('importPage.unlimitedRequired')}</p>
        <p style={{ marginBottom: 0, color: 'var(--text-dim)' }}>
          <a href="https://bandzontour.com" target="_blank" rel="noopener noreferrer">
            {t('account.viewPlans')} →
          </a>
        </p>
      </div>
    );
  }

  const hasKey = tmStatus?.hasKey === true;

  return (
    <div>
      <p style={{ marginTop: 0, color: 'var(--text-dim)' }}>
        {t('importPage.intro', { band: label })}
      </p>

      {/* ---- 1. TM API key ---- */}
      <div className="form-section" style={{ maxWidth: 520 }}>
        <h3>{t('importPage.tmKeyTitle')}</h3>
        <p style={{ marginTop: 0, color: 'var(--text-dim)' }}>
          {t('importPage.tmKeyHint')}
        </p>

        {hasKey && (
          <p style={{ color: 'var(--success)', fontSize: '0.9rem' }}>
            {t('importPage.keyPresent')}
          </p>
        )}
        {keyMsg && <p style={{ color: 'var(--success)', fontSize: '0.9rem' }}>{keyMsg}</p>}
        {keyErr && <p style={{ color: 'var(--danger)', fontSize: '0.9rem' }}>{keyErr}</p>}

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 220px', minWidth: 0 }}>
            <label>{t('importPage.tmApiKey')}</label>
            <PasswordInput
              className="form-input"
              autoComplete="off"
              value={keyInput}
              placeholder={hasKey ? t('importPage.keyMaskedPlaceholder') : t('importPage.keyPlaceholder')}
              disabled={!canWrite}
              onChange={(e) => setKeyInput(e.target.value)}
            />
          </div>
          <button
            className="btn btn-primary"
            onClick={saveKey}
            disabled={keyBusy || !keyInput.trim() || keyInput.trim().startsWith('***') || !canWrite}
          >
            {keyBusy ? t('importPage.savingKey') : t('importPage.saveKey')}
          </button>
          {hasKey && (
            <button
              className="btn btn-secondary"
              onClick={deleteKey}
              disabled={keyBusy || !canWrite}
            >
              {t('importPage.deleteKey')}
            </button>
          )}
        </div>
      </div>

      {/* ---- 2. TM one-click import ---- */}
      <div className="form-section" style={{ maxWidth: 520 }}>
        <h3>{t('importPage.tmImportTitle')}</h3>
        <p style={{ marginTop: 0, color: 'var(--text-dim)' }}>
          {t('importPage.tmImportHint', { band: label })}
        </p>

        {tmResult && <p style={{ color: 'var(--success)', fontSize: '0.9rem' }}>{tmResult}</p>}
        {tmErr && <p style={{ color: 'var(--danger)', fontSize: '0.9rem' }}>{tmErr}</p>}

        <button
          className="btn btn-primary"
          onClick={runTmImport}
          disabled={tmRunning || !hasKey || !canWrite}
          title={hasKey ? '' : t('importPage.saveKeyFirstTitle')}
        >
          {tmRunning ? t('importPage.importing') : t('importPage.import')}
        </button>
        {!hasKey && (
          <small style={{ color: 'var(--text-dim)', marginLeft: 10 }}>
            {t('importPage.saveKeyFirst')}
          </small>
        )}
      </div>

      {/* ---- 3. MasterTour CSV import ---- */}
      <div className="form-section" style={{ maxWidth: 520 }}>
        <h3>{t('importPage.mtCsvTitle')}</h3>
        <p style={{ marginTop: 0, color: 'var(--text-dim)' }}>
          {t('importPage.mtCsvHint')}
        </p>

        {csvResult && <p style={{ color: 'var(--success)', fontSize: '0.9rem' }}>{csvResult}</p>}
        {csvErr && <p style={{ color: 'var(--danger)', fontSize: '0.9rem' }}>{csvErr}</p>}

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => setCsvFile(e.target.files?.[0] ?? null)}
            disabled={!canWrite}
            style={{ flex: '1 1 220px', minWidth: 0 }}
          />
          <button
            className="btn btn-primary"
            onClick={importMtCsv}
            disabled={csvImporting || !csvFile || !canWrite}
          >
            {csvImporting ? t('importPage.importing') : t('importPage.import')}
          </button>
        </div>
        {csvFile && (
          <small style={{ color: 'var(--text-dim)' }}>{t('importPage.fileSelected', { name: csvFile.name })}</small>
        )}
      </div>
    </div>
  );
}
